import React from 'react';
import { useCurrentBillingInfoQuery } from '$/GraphQL/schema/schema';
import { css } from '@emotion/react';

import { ReactComponent as AMexCardIcon } from '$/images/cards/amex.svg';
import { ReactComponent as DinersCardIcon } from '$/images/cards/diners.svg';
import { ReactComponent as DiscoverCardIcon } from '$/images/cards/discover.svg';
import { ReactComponent as JCBCardIcon } from '$/images/cards/jcb.svg';
import { ReactComponent as MasterCardIcon } from '$/images/cards/mastercard.svg';
import { ReactComponent as UnionPayCardIcon } from '$/images/cards/unionpay.svg';
import { ReactComponent as VisaCardIcon } from '$/images/cards/visa.svg';
import { ReactComponent as DefaultCardIcon } from '$/images/cards/default-card.svg';
import { usePlanPageCTAButtons } from './PlanPageCTAButtons';
import Settings from '$/components/ui/Settings';

const CardIcon: React.FC<{ type?: string | null }> = ({ type }) => {
  let Icon = DefaultCardIcon;
  switch (type) {
    case 'amex':
      Icon = AMexCardIcon;
      break;
    case 'diners':
      Icon = DinersCardIcon;
      break;
    case 'discover':
      Icon = DiscoverCardIcon;
      break;
    case 'jcb':
      Icon = JCBCardIcon;
      break;
    case 'mastercard':
      Icon = MasterCardIcon;
      break;
    case 'unionpay':
      Icon = UnionPayCardIcon;
      break;
    case 'visa':
      Icon = VisaCardIcon;
      break;
  }
  return <Icon className='w-8 h-8 mr-2 md:mr-4' />;
};

export const BillingAndCard: React.FC = () => {
  const currentBillingInfoQuery = useCurrentBillingInfoQuery();
  const { buttons } = usePlanPageCTAButtons();

  const info = React.useMemo(() => {
    const cardInfo = currentBillingInfoQuery.data?.currentBillingInfo.card;
    const billingInfo = currentBillingInfoQuery.data?.currentBillingInfo;

    if (!cardInfo || !billingInfo) return null;

    return (
      <table
        className='ml-1/2e'
        css={css`
          td {
            vertical-align: top;
            &:last-child {
              padding-left: 0.5rem;
            }
          }
        `}
      >
        <thead>
          <tr>
            <td className='pb-1/2e'>Credit card on file</td>
            <td className='pb-1/2e'>Billing address</td>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className='inline-flex items-center font-mono text-sm'>
              <CardIcon type={cardInfo.brand} />
              <div className='tracking-wide whitespace-no-wrap'>
                **** **** **** {cardInfo.last4}
              </div>
            </td>
            <td>{[billingInfo.address1, billingInfo.address2].filter(Boolean).join(', ')}</td>
          </tr>
          <tr>
            <td>
              <div className='flex items-center'>
                <div>
                  Expires {cardInfo.exp_month}/{cardInfo.exp_year}
                </div>
              </div>
            </td>
            <td>
              {[billingInfo.city, billingInfo.province, billingInfo.country]
                .filter(Boolean)
                .join(', ')}{' '}
              {billingInfo.postal}
            </td>
          </tr>
          <tr>
            <td className='align-middle'>{buttons.updatePaymentDetails}</td>
            <td className='align-middle'>{buttons.updateBillingAddress}</td>
          </tr>
        </tbody>
      </table>
    );
  }, [currentBillingInfoQuery.data?.currentBillingInfo, buttons]);

  return (
    <>
      <Settings.Title>Payment details</Settings.Title>
      <div
        className='flex flex-col items-left mt-1e'
        css={css`
          min-width: 12rem;
        `}
      >
        {info}
      </div>
    </>
  );
};
